import { designTokens, RiCircleFill } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PDFViewer } from "@itandi/itandi-bb-ui/PDFViewer";
import { YokuaruBody } from "@itandi/itandi-bb-ui/YokuaruBody";
import { YokuaruFooter } from "@itandi/itandi-bb-ui/YokuaruFooter";
import { YokuaruHeader } from "@itandi/itandi-bb-ui/YokuaruHeader";
import { useState } from "react";
import { BackToEditConfirmModal } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/BackToEditConfirmModal";
import { useDynamicHeight } from "~/hooks/useDynamicHeight";
import type { DealEstimationOcrRequest } from "~/models/DealEstimationOcrRequest";
import { getFilePath } from "~/utils/api/deals/DealEstimationOcrRequest";
import { toThousandSeparator } from "~/utils/CurrencySeparator";

type Props = {
	backToEditPath: string;
	ocrRequest: DealEstimationOcrRequest;
};

const PDF_PANE_WIDTH = "45%";
export function SucceededState({ backToEditPath, ocrRequest }: Props) {
	const filePath = getFilePath(ocrRequest.dealId, ocrRequest.id);
	const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen] = useState(false);
	const { height: columnsHeight, ref: columnsRef } =
		useDynamicHeight<HTMLDivElement>();
	const { height: panelHeight, ref: panelRef } =
		useDynamicHeight<HTMLDivElement>();

	return (
		<Grid
			gridTemplateColumns={`${PDF_PANE_WIDTH} 1fr`}
			height={columnsHeight}
			ref={columnsRef}
			width="100%"
		>
			<PDFViewer
				height="100%"
				src={filePath}
				title="見積書原本PDF"
				width="100%"
			/>
			<div>
				<YokuaruHeader
					heading={<Heading as="h1">読み取り結果の確認・修正</Heading>}
					sticky
				/>
				<div ref={panelRef} style={{ height: panelHeight, overflowY: "auto" }}>
					<YokuaruBody>
						<Panel>
							<PanelBody>
								<Grid gap={designTokens["spacing/m"]}>
									<Flex alignItems="center" gap={designTokens["spacing/l"]}>
										<Grid
											alignItems="center"
											columnGap={designTokens["spacing/xxs"]}
											gridTemplateColumns="auto auto auto"
											rowGap={designTokens["spacing/xs"]}
											width="fit-content"
										>
											<RiCircleFill
												color={
													designTokens[
														"color/chips/presentation/foreground/red"
													]
												}
												size="0.75rem"
											/>
											<Label size="s">提案項目を除く小計: </Label>
											<Flex justifyContent="flex-end">
												<Label size="s" weight="bold">
													{`¥${toThousandSeparator(0) ?? "0"}`}
												</Label>
											</Flex>
											<RiCircleFill
												color={
													designTokens[
														"color/chips/presentation/foreground/blue"
													]
												}
												size="0.75rem"
											/>
											<Label size="s">提案項目小計: </Label>
											<Flex justifyContent="flex-end">
												<Label size="s" weight="bold">
													{`¥${toThousandSeparator(0) ?? "0"}`}
												</Label>
											</Flex>
										</Grid>
										<Flex
											direction="column"
											flexShrink={0}
											gap={designTokens["spacing/xxs"]}
										>
											<Label size="s">税抜き合計金額</Label>
											<Label size="xl" weight="bold">
												{`¥${toThousandSeparator(0) ?? "0"}`}
											</Label>
										</Flex>
									</Flex>
									<Alertv2 variant="info">
										<Paragraph>
											AIによる読み取り結果には誤りが含まれる場合があります。
											<br />
											原本PDFをご確認の上、必要に応じて修正・追加してください。
										</Paragraph>
									</Alertv2>
									{/* デバッグ表示 */}
									<pre>{JSON.stringify(ocrRequest.items, null, 2)}</pre>
								</Grid>
							</PanelBody>
						</Panel>
					</YokuaruBody>
				</div>
				<YokuaruFooter
					floatingMenu={
						<FloatingMenu
							fitToParent
							rightArea={
								<Flex alignItems="center" gap={designTokens["spacing/s"]}>
									<Buttonv2
										onClick={() => {
											setIsLeaveConfirmModalOpen(true);
										}}
										variant="secondary"
									>
										見積作成画面へもどる
									</Buttonv2>
									<Buttonv2 disabled variant="primary">
										見積に反映
									</Buttonv2>
								</Flex>
							}
						/>
					}
				/>
			</div>
			<BackToEditConfirmModal
				backToEditPath={backToEditPath}
				isOpen={isLeaveConfirmModalOpen}
				onClose={() => {
					setIsLeaveConfirmModalOpen(false);
				}}
			/>
		</Grid>
	);
}
